import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { HiOutlineLocationMarker, HiOutlineShieldCheck, HiOutlineBell } from 'react-icons/hi';
import { Card, Button, Input, Spinner } from '../../components/UI';
import { visitsAPI } from '../../services/api';
import VisitOutcomes from './VisitOutcomes';

/**
 * Settings → Visits
 * Approval rule for visits telecallers schedule, plus reminder / late-alert
 * timing. GPS at Start/End is set per user in Users → Edit → Visit permissions.
 */
const VisitSettings = () => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ requireApproval: false, reminderMinutes: 30, lateAlertMinutes: 15 });
  const [saving, setSaving] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['visit-settings'],
    queryFn: async () => (await visitsAPI.getSettings()).data?.data,
  });

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  const dirty =
    !!data &&
    (data.requireApproval !== form.requireApproval ||
      String(data.reminderMinutes) !== String(form.reminderMinutes) ||
      String(data.lateAlertMinutes) !== String(form.lateAlertMinutes));

  const save = async () => {
    setSaving(true);
    try {
      const res = await visitsAPI.updateSettings(form);
      queryClient.setQueryData(['visit-settings'], res.data?.data);
      toast.success('Visit settings saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 p-4 rounded-xl bg-blue-50 border border-blue-100">
        <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0">
          <HiOutlineLocationMarker className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Visits & Meetings</h3>
          <p className="text-sm text-gray-600 mt-0.5">
            Rules for site visits and customer meetings. Whether GPS is required at Start / End is set
            per employee in Users → Edit → Visit permissions.
          </p>
        </div>
      </div>

      <Card padding="p-4 lg:p-5" className="space-y-5">
        {/* Approval */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <HiOutlineShieldCheck className="w-5 h-5 text-primary-600 mt-0.5 flex-shrink-0" />
            <div>
              <p id="visit-approval-label" className="text-sm font-semibold text-secondary-900">
                Require approval for visits scheduled by telecallers
              </p>
              <p className="text-xs text-secondary-500 mt-0.5">
                When on, a visit a telecaller schedules waits as "Pending Approval" until an admin or manager
                approves it. Visits scheduled by admins and managers are always approved.
              </p>
            </div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={form.requireApproval}
            aria-labelledby="visit-approval-label"
            onClick={() => setForm((f) => ({ ...f, requireApproval: !f.requireApproval }))}
            className={clsx(
              'relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors',
              form.requireApproval ? 'bg-success-500' : 'bg-secondary-300'
            )}
          >
            <span
              className={clsx(
                'absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform',
                form.requireApproval && 'translate-x-5'
              )}
            />
          </button>
        </div>

        <div className="border-t border-secondary-100" />

        {/* Timing */}
        <div className="flex items-start gap-3">
          <HiOutlineBell className="w-5 h-5 text-primary-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1 space-y-3">
            <div>
              <p className="text-sm font-semibold text-secondary-900">Reminders & alerts</p>
              <p className="text-xs text-secondary-500 mt-0.5">
                Used by the visit reminder notifications and the on-time % in reports.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
              <Input
                label="Remind employee before visit (minutes)"
                type="number"
                min={0}
                max={1440}
                value={form.reminderMinutes}
                onChange={(e) => setForm((f) => ({ ...f, reminderMinutes: e.target.value }))}
              />
              <Input
                label="Late alert after start time (minutes)"
                type="number"
                min={0}
                max={1440}
                value={form.lateAlertMinutes}
                onChange={(e) => setForm((f) => ({ ...f, lateAlertMinutes: e.target.value }))}
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-2 border-t border-secondary-100">
          <Button onClick={save} loading={saving} disabled={!dirty}>
            Save Settings
          </Button>
        </div>
      </Card>

      <VisitOutcomes />
    </div>
  );
};

export default VisitSettings;
